import type { SSEConnection } from '@euler/lib/request/SSEClient';
import {
  AIMessageInfo,
  AIPageContext,
  AiSessionType,
  kAiClientFeatures,
} from '@euler/model/ai';
import { AiAgentBundleId, AiHomeDispatchMode } from '@euler/model/ai-home';
import {
  AiAttachmentInfo,
  ApdmStepAnswer,
  isAiAttachmentId,
} from '@euler/model/apdm';
import { BiAnswer } from '@euler/model/bi/answer';
import type { AIAssistantService } from '@euler/services/ai-assistant.service';
import { BehaviorSubject, Subject } from 'rxjs';

export enum AiChatReplyStatus {
  Streaming = 'streaming',
  Done = 'done',
  Stopped = 'stopped',
  Failed = 'failed',
}

export type AiChatMessage =
  | {
      key: string;
      role: 'user';
      content: string;
      attachments: AiAttachmentInfo[];
      /** History only carries ids; the bubble reads their metadata. */
      attachmentIds: string[];
      /**
       * Sent by the app for something that happened (a sheet finished
       * importing), not typed by the user: it shows no bubble.
       */
      event: boolean;
      /** Epoch milliseconds. */
      createdAt: number;
    }
  | {
      key: string;
      role: 'assistant';
      id?: string;
      content: string;
      status: AiChatReplyStatus;
      /** Whether this reply streamed in this visit (not read from history). */
      live: boolean;
      /** Epoch milliseconds. */
      createdAt: number;
      reaction: AiChatReaction | null;
    };

export type AiChatReaction = 'up' | 'down';

/**
 * A tapped card's answer, sent with the message so the server settles it
 * before (or instead of) a model reply.
 */
export type AiChatTap =
  | { kind: 'apdm'; answer: ApdmStepAnswer }
  | { kind: 'bi'; answer: BiAnswer }
  /** A library case picked in the app, opening a coaching conversation. */
  | { kind: 'coaching_case'; caseId: string };

/**
 * One kind of work the reply is doing, as the user reads it: the calls of
 * one tool merge into one line, done when every one of them is.
 */
export interface AiChatStep {
  /** "正在读取您的表格…" while it runs. */
  label: string;
  total: number;
  done: number;
}

export interface AiChatState {
  messages: AiChatMessage[];
  sessionId?: string;
  bundleId?: AiAgentBundleId;
  busy: boolean;
  loading: boolean;
  hasMore: boolean;
  /** A plain-language line for the work in progress, e.g. "正在读取表格…". */
  working: string | null;
  /** What the reply being written has done so far, in order. */
  steps: AiChatStep[];
  loadError: string | null;
}

const kInitial: AiChatState = {
  messages: [],
  busy: false,
  loading: false,
  hasMore: false,
  working: null,
  steps: [],
  loadError: null,
};

/** Tokens arrive faster than a list can re-render; flush on this cadence. */
const kFlushMs = 100;
/**
 * Text arrives in bursts — a sentence at once when a lookup starts, a
 * paragraph in a second after the model is done — so it is revealed at a
 * typing pace instead: a few characters a tick, more as it backs up, every
 * burst out within about a second.
 */
const kRevealMs = 50;
const kRevealMinChars = 3;
const kRevealTicks = 20;

/**
 * The next piece of a reply to reveal: about `size` characters of prose,
 * but a fence goes out whole — half a fence would show as raw text.
 */
function nextRevealPiece(pending: string, size: number): string {
  if (pending.startsWith('```')) {
    const close = pending.indexOf('```', 3);
    return close < 0 ? pending : pending.slice(0, close + 3);
  }
  const fence = pending.indexOf('```');
  const end = Math.min(size, fence < 0 ? pending.length : fence);
  return pending.slice(0, Math.max(end, 1));
}

const kWorkingLabels: Array<[RegExp, string]> = [
  [/^bi_propose_insights/, '正在挑选值得看的图表…'],
  [/^bi_(start|get)_upload/, '正在读取您的表格…'],
  [/^bi_list_datasets/, '正在查找您的数据…'],
  [/^bi_describe_dataset/, '正在查看表格的字段…'],
  [/^bi_run_query/, '正在查询数据…'],
  [/import/, '正在读取您的表格…'],
  [/coupon/, '正在整理优惠券活动…'],
  [/preview/, '正在核算可触达的客户…'],
  [/audience|insight/, '正在筛选客户…'],
  [/draft|content|schedule/, '正在拟定活动…'],
];

function workingLabel(tool: string): string {
  return (
    kWorkingLabels.find(([pattern]) => pattern.test(tool))?.[1] ??
    '正在处理您的请求…'
  );
}

function historyMessages(messages: AIMessageInfo[]): AiChatMessage[] {
  return messages.flatMap((message): AiChatMessage[] => {
    if (message.role === 'assistant')
      return [
        {
          key: message.id,
          id: message.id,
          role: 'assistant',
          content: message.content,
          status: AiChatReplyStatus.Done,
          live: false,
          createdAt: new Date(message.createdAt).getTime(),
          reaction: null,
        },
      ];
    if (message.role !== 'user') return [];
    let metadata: unknown = message.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch {
        metadata = null;
      }
    }
    // A tapped suggestion carries the files of the summary above it, which
    // already shows them.
    const refs =
      metadata &&
      typeof metadata === 'object' &&
      'attachments' in metadata &&
      Array.isArray(metadata.attachments) &&
      !('attachmentsCarried' in metadata && metadata.attachmentsCarried)
        ? metadata.attachments
        : [];
    return [
      {
        key: message.id,
        role: 'user',
        content: message.content,
        attachments: [],
        createdAt: new Date(message.createdAt).getTime(),
        event:
          !!metadata &&
          typeof metadata === 'object' &&
          'apdmAnswer' in metadata &&
          isAppEventAnswer(metadata.apdmAnswer),
        attachmentIds: refs.flatMap((ref: unknown) =>
          ref &&
          typeof ref === 'object' &&
          'attachmentId' in ref &&
          isAiAttachmentId(ref.attachmentId)
            ? [ref.attachmentId]
            : [],
        ),
      },
    ];
  });
}

/**
 * An answer the app sends for an event rather than the user's tap: a sheet
 * finished importing, or a card launched, paused, resumed or stopped the
 * campaign.
 */
function isAppEventAnswer(answer: unknown): boolean {
  return (
    !!answer &&
    typeof answer === 'object' &&
    (('importId' in answer && typeof answer.importId === 'string') ||
      ('campaignId' in answer && typeof answer.campaignId === 'string'))
  );
}

/**
 * One 知车 AI conversation: the transcript, the streaming reply and the
 * session the server put it in. A failed or stopped reply never locks the
 * chat; the user can retry or simply keep talking.
 */
export class AiChatConversation {
  readonly state$ = new BehaviorSubject<AiChatState>(kInitial);
  /**
   * A tap whose reply ended: the server settled it (or failed to), so what
   * it touched — a dashboard, a draft's placements — may have changed.
   */
  readonly tapSettled$ = new Subject<AiChatTap>();
  private connection: SSEConnection | null = null;
  private generation = 0;
  private beforeId: string | undefined = undefined;
  private disposed = false;
  private pending = '';
  /** The tool calls still running, oldest first, with their step's label. */
  private running: Array<{ name: string; label: string }> = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** What waits for the reveal to catch up: the reply's end. */
  private afterReveal: (() => void) | null = null;
  private lastTurn: {
    text: string;
    attachments: AiAttachmentInfo[];
    tap?: AiChatTap;
  } | null = null;

  constructor(
    private readonly service: Pick<
      AIAssistantService,
      'ask' | 'getMessages' | 'toggleReaction' | 'getSessionForPage'
    >,
    private readonly sessionSaved?: (sessionId: string) => void,
    /**
     * The page every turn is asked from, e.g. a dashboard. Such a chat
     * talks only in the page's own session, opened before the first turn.
     */
    private readonly pageContext?: AIPageContext,
  ) {}

  /** Back to an empty chat, optionally focused on one agent. */
  reset(bundleId?: AiAgentBundleId) {
    this.cancel();
    this.beforeId = undefined;
    this.lastTurn = null;
    this.state$.next({ ...kInitial, bundleId });
  }

  dispose() {
    this.cancel();
    this.disposed = true;
    this.state$.complete();
    this.tapSettled$.complete();
  }

  stop() {
    // Revealing the rest may end a reply that already finished; read what
    // is still running after it.
    this.flush(true);
    const key = this.replyKey();
    // The server settles a tap before any reply, so a stopped reply still
    // changed what the tap was for.
    const tap = key ? this.lastTurn?.tap : undefined;
    this.cancel();
    this.update({
      busy: false,
      working: null,
      messages: this.state$.value.messages.map(message =>
        message.key === key && message.role === 'assistant'
          ? { ...message, status: AiChatReplyStatus.Stopped }
          : message,
      ),
    });
    if (tap) this.tapSettled$.next(tap);
  }

  async open(sessionId: string, bundleId?: AiAgentBundleId) {
    this.reset(bundleId);
    const generation = this.generation;
    this.update({ loading: true, sessionId });
    try {
      const page = await this.service.getMessages(sessionId, { limit: 100 });
      if (this.disposed || generation !== this.generation) return;
      this.beforeId = page.messages[0]?.id;
      this.update({
        hasMore: page.hasMore,
        messages: historyMessages(page.messages),
      });
    } catch (error) {
      console.error('AI messages failed', sessionId, error);
      if (generation === this.generation)
        this.update({
          loadError: this.pageContext
            ? '暂时无法读取这段对话，请关闭后重新打开。'
            : '无法读取这段对话，请返回后重试。',
        });
    } finally {
      if (generation === this.generation) this.update({ loading: false });
    }
  }

  /** Opens the page's own conversation, the one every turn here goes to. */
  async openPage() {
    if (!this.pageContext) return;
    this.reset(this.state$.value.bundleId);
    const generation = this.generation;
    this.update({ loading: true });
    const session = await this.service.getSessionForPage(
      AiSessionType.WorkshopAssistant,
      this.pageContext,
    );
    if (this.disposed || generation !== this.generation) return;
    if (!session) {
      console.error('AI page session failed', this.pageContext);
      this.update({
        loading: false,
        loadError: '暂时无法打开这段对话，请关闭后重新打开。',
      });
      return;
    }
    await this.open(session.id, this.state$.value.bundleId);
  }

  async earlier() {
    const state = this.state$.value;
    if (state.loading || !state.hasMore || !state.sessionId || !this.beforeId)
      return;
    const generation = this.generation;
    this.update({ loading: true });
    try {
      const page = await this.service.getMessages(state.sessionId, {
        limit: 100,
        beforeId: this.beforeId,
      });
      if (this.disposed || generation !== this.generation) return;
      this.beforeId = page.messages[0]?.id;
      this.update({
        hasMore: page.hasMore && !!this.beforeId,
        messages: [
          ...historyMessages(page.messages),
          ...this.state$.value.messages,
        ],
      });
    } catch {
      // The older page stays reachable: the next scroll to the top retries.
    } finally {
      if (generation === this.generation) this.update({ loading: false });
    }
  }

  /** Thumbs up or down; the same thumb again takes it back. */
  react(key: string, reaction: AiChatReaction) {
    const message = this.state$.value.messages.find(item => item.key === key);
    if (message?.role !== 'assistant' || !message.id) return;
    const next = message.reaction === reaction ? null : reaction;
    this.update({
      messages: this.state$.value.messages.map(item =>
        item.key === key && item.role === 'assistant'
          ? { ...item, reaction: next }
          : item,
      ),
    });
    // The server toggles: sending the current thumb again clears it.
    this.service.toggleReaction(message.id, reaction).catch(error => {
      console.error('AI reaction failed', error);
    });
  }

  /** Sends the last turn again after it failed or was stopped. */
  retry(): boolean {
    const messages = this.state$.value.messages;
    const reply = messages[messages.length - 1];
    if (
      !this.lastTurn ||
      reply?.role !== 'assistant' ||
      ![AiChatReplyStatus.Failed, AiChatReplyStatus.Stopped].includes(
        reply.status,
      )
    )
      return false;
    // The retried turn replaces the unfinished one rather than repeating it.
    this.update({ messages: messages.slice(0, -2) });
    const { text, attachments, tap } = this.lastTurn;
    return this.send(text, attachments, tap);
  }

  /**
   * `tap` is a tapped card's answer — a campaign build step, or a BI
   * widget's action: the server records it with the message and settles it
   * before any reply.
   */
  send(
    text: string,
    attachments: AiAttachmentInfo[],
    tap?: AiChatTap,
  ): boolean {
    const state = this.state$.value;
    const message = text.trim();
    if (
      this.disposed ||
      state.busy ||
      state.loading ||
      (!message && !attachments.length) ||
      attachments.length > 5 ||
      // A BI tap answers a widget of a conversation that already exists.
      (tap?.kind === 'bi' && !state.sessionId) ||
      (this.pageContext && !state.sessionId)
    )
      return false;
    this.lastTurn = { text: message, attachments, tap };
    const generation = ++this.generation;
    const key = `reply-${generation}`;
    const live = () => !this.disposed && generation === this.generation;
    this.running = [];
    this.update({
      busy: true,
      working: '正在思考…',
      steps: [],
      messages: [
        ...state.messages,
        {
          key: `user-${generation}`,
          role: 'user',
          content: message,
          attachments,
          attachmentIds: [],
          event: tap?.kind === 'apdm' && isAppEventAnswer(tap.answer),
          createdAt: Date.now(),
        },
        {
          key,
          role: 'assistant',
          content: '',
          status: AiChatReplyStatus.Streaming,
          live: true,
          createdAt: Date.now(),
          reaction: null,
        },
      ],
    });
    const finish = (change: { status: AiChatReplyStatus; id?: string }) => {
      // The reply ends once what arrived is on screen, typed out like the
      // rest rather than popping in.
      if (this.pending) {
        this.afterReveal = () => finish(change);
        this.flushTimer ??= setTimeout(() => this.flush(), kRevealMs);
        return;
      }
      this.update({
        busy: false,
        working: null,
        messages: this.state$.value.messages.map(item =>
          item.key === key && item.role === 'assistant'
            ? { ...item, ...change }
            : item,
        ),
      });
      if (tap && !this.disposed) this.tapSettled$.next(tap);
    };
    this.connection = this.service.ask(
      {
        sessionType: AiSessionType.WorkshopAssistant,
        message,
        attachments: attachments.map(info => ({ attachmentId: info.id })),
        ...(tap?.kind === 'apdm' && state.sessionId
          ? { apdmAnswer: tap.answer }
          : {}),
        ...(tap?.kind === 'bi' ? { biAnswer: tap.answer } : {}),
        ...(tap?.kind === 'coaching_case' && !state.sessionId
          ? { coachingCaseId: tap.caseId }
          : {}),
        ...(this.pageContext ? { pageContext: this.pageContext } : {}),
        clientFeatures: kAiClientFeatures,
        ...(state.sessionId
          ? { sessionId: state.sessionId }
          : state.bundleId
          ? { bundleId: state.bundleId }
          : { homeDispatch: AiHomeDispatchMode.Automatic }),
      },
      {
        onSession: sessionId => {
          if (!live()) return;
          this.update({ sessionId });
          this.sessionSaved?.(sessionId);
        },
        onToken: chunk => {
          if (!live()) return;
          this.pending += chunk;
          this.flushTimer ??= setTimeout(() => this.flush(), kFlushMs);
        },
        onToolCall: (name, _args, progress) => {
          if (!live()) return;
          const label = progress ?? workingLabel(String(name));
          this.running.push({ name: String(name), label });
          const steps = this.state$.value.steps;
          this.update({
            working: label,
            steps: steps.some(step => step.label === label)
              ? steps.map(step =>
                  step.label === label
                    ? { ...step, total: step.total + 1 }
                    : step,
                )
              : [...steps, { label, total: 1, done: 0 }],
          });
        },
        // Once every tool is done, what follows is the model deciding its
        // next move, which can take a while and is no part of their work.
        onToolResult: name => {
          if (!live()) return;
          const index = this.running.findIndex(call => call.name === name);
          if (index < 0) return;
          const [{ label }] = this.running.splice(index, 1);
          this.update({
            ...(this.running.length ? {} : { working: '正在思考…' }),
            steps: this.state$.value.steps.map(step =>
              step.label === label ? { ...step, done: step.done + 1 } : step,
            ),
          });
        },
        onDone: (messageId, sessionId) => {
          if (!live()) return;
          this.connection = null;
          this.update({ sessionId });
          finish({ status: AiChatReplyStatus.Done, id: messageId });
          this.sessionSaved?.(sessionId);
        },
        onError: () => {
          if (!live()) return;
          this.connection = null;
          finish({ status: AiChatReplyStatus.Failed });
        },
      },
    );
    return true;
  }

  private replyKey(): string | undefined {
    const last =
      this.state$.value.messages[this.state$.value.messages.length - 1];
    return last?.role === 'assistant' &&
      last.status === AiChatReplyStatus.Streaming
      ? last.key
      : undefined;
  }

  /** Reveals the next piece of what arrived, or all of it once it ended. */
  private flush(all = false) {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    if (!this.pending) return;
    const key = this.replyKey();
    const chunk = all
      ? this.pending
      : nextRevealPiece(
          this.pending,
          Math.max(
            kRevealMinChars,
            Math.ceil(this.pending.length / kRevealTicks),
          ),
        );
    this.pending = this.pending.slice(chunk.length);
    if (this.pending)
      this.flushTimer = setTimeout(() => this.flush(), kRevealMs);
    if (key)
      this.update({
        messages: this.state$.value.messages.map(message =>
          message.key === key && message.role === 'assistant'
            ? { ...message, content: message.content + chunk }
            : message,
        ),
      });
    if (!this.pending && this.afterReveal) {
      const next = this.afterReveal;
      this.afterReveal = null;
      next();
    }
  }

  private cancel() {
    this.generation++;
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.pending = '';
    this.afterReveal = null;
    this.connection?.abort();
    this.connection = null;
  }

  private update(change: Partial<AiChatState>) {
    if (!this.disposed) this.state$.next({ ...this.state$.value, ...change });
  }
}
